import { c as _c } from "react/compiler-runtime";
import { type ActionFlow, type ActionFlowState, useActionFlow } from '@repo/web3';
import { useLayoutEffect, useRef } from 'react';
import { useIsActionFlowHeld } from '@/components/providers/VaultsActionFlowProvider';
export function canReplaceActionFlow(state: ActionFlowState) {
  return state.flowState === 'idle' && state.stepIndex === 0 && !('errorMessage' in state);
}

/**
 * Drops rebuilds during transactions, after step zero, or with an error.
 * Callers rebuild on every block, so the next idle step-zero rebuild is adopted.
 */
export function useActionFlowRegistration() {
  const $ = _c(12);
  const {
    state,
    reset,
    setActionFlow
  } = useActionFlow();
  const isHeld = useIsActionFlowHeld();
  const stateRef = useRef(state);
  const heldRef = useRef(isHeld);
  let t0;
  if ($[0] !== isHeld || $[1] !== state) {
    t0 = () => {
      stateRef.current = state;
      heldRef.current = isHeld;
    };
    $[0] = isHeld;
    $[1] = state;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  useLayoutEffect(t0);
  let t1;
  if ($[3] !== reset || $[4] !== setActionFlow) {
    t1 = flow => {
      if (!canReplaceActionFlow(stateRef.current) || heldRef.current) {
        return false;
      }
      reset();
      setActionFlow(flow);
      return true;
    };
    $[3] = reset;
    $[4] = setActionFlow;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const registerActionFlow = t1;
  let t2;
  if ($[6] !== isHeld || $[7] !== state) {
    t2 = canReplaceActionFlow(state) && !isHeld;
    $[6] = isHeld;
    $[7] = state;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const canReplace = t2;
  let t3;
  if ($[9] !== canReplace || $[10] !== registerActionFlow) {
    t3 = {
      registerActionFlow,
      canReplace
    };
    $[9] = canReplace;
    $[10] = registerActionFlow;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}